import React from 'react'
import { AccordionCard } from 'components/AccordionCard'
import {
  Box,
  Button,
  Flex,
  Label,
  Loading,
  Message,
  Text,
  toast,
  useAppTheme,
} from '@workwhile/ui'
import { Pencil, Plus, Trash } from 'lucide-react'
import { format, formatDistanceToNowStrict, isAfter, parseISO } from 'date-fns'
import { parseErrorMessage } from 'api'
import { getLongDuration } from 'lib/time'
import { ButtonWithConfirmation } from 'pages/Dayforce/shared/components/ButtonWithConfirmation'
import { useCushionChargePoliciesContextValue } from '../context/CushionChargePoliciesProvider'
import {
  CushionChargePolicyItem,
  useCushionChargePoliciesWorkflowsContextValue,
} from '../context/CushionChargePoliciesWorkflowsProvider'

function policyStatus(policy: CushionChargePolicyItem) {
  const now = new Date()
  if (policy.endsAt != null && !isAfter(parseISO(policy.endsAt), now)) {
    return 'Ended'
  }
  if (isAfter(parseISO(policy.startsAt), now)) {
    return 'Upcoming'
  }
  return 'Active'
}

function chargeAfterLabel(chargeAfterMinutes: number) {
  const duration = getLongDuration(chargeAfterMinutes)
  if (!duration) {
    return 'Charge after 0 mins'
  }
  return `Charge after ${duration}`
}

function describeWhen(date: Date, pastLabel: string, futureLabel: string) {
  const formatted = format(date, 'MMM d, yyyy h:mm a')
  const distance = formatDistanceToNowStrict(date, { addSuffix: true })
  const label = isAfter(date, new Date()) ? futureLabel : pastLabel
  return `${label} ${formatted} (${distance})`
}

const PolicyRow = ({
  policy,
  isLast,
}: {
  policy: CushionChargePolicyItem
  isLast: boolean
}) => {
  const { radii } = useAppTheme()
  const {
    state: { isDeleting },
    actions: { deletePolicy },
  } = useCushionChargePoliciesContextValue()
  const {
    editCushionChargePolicyFlow: { open: openEdit },
  } = useCushionChargePoliciesWorkflowsContextValue()

  const status = policyStatus(policy)
  const isUpcoming = status === 'Upcoming'
  const startsAtLabel = describeWhen(
    parseISO(policy.startsAt),
    'Active since',
    'Starts'
  )
  const endsAtLabel = policy.endsAt
    ? describeWhen(parseISO(policy.endsAt), 'Ended', 'Ends')
    : null
  const statusVariant =
    status === 'Active'
      ? 'success'
      : status === 'Upcoming'
        ? 'information'
        : 'neutral'

  const handleDelete = () => {
    deletePolicy(policy.id, {
      onSuccess: () => {
        toast.success(
          isUpcoming
            ? 'Cushion charge policy deleted'
            : 'Cushion charge policy ended',
          { position: 'top-center' }
        )
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error), { position: 'top-center' })
      },
    })
  }

  return (
    <Box
      display="block"
      border="1px solid"
      borderColor="neutrals.100"
      p={3}
      borderRadius={radii.standard}
      width="100%"
      mb={isLast ? 0 : 3}
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box>
          <Label mb={2} variant={statusVariant} size="small">
            {status}
          </Label>
          <Text>{startsAtLabel}</Text>
          {endsAtLabel ? (
            <Text fontSize={1} color="lightText" mt={1}>
              {endsAtLabel}
            </Text>
          ) : null}
          <Text fontSize={1} color="lightText" mt={2}>
            {chargeAfterLabel(policy.chargeAfterMinutes)}
          </Text>
        </Box>
        <Flex alignItems="center" gap={1}>
          <Button
            variant="text"
            kind="small"
            aria-label="Edit cushion charge policy"
            title="Edit cushion charge policy"
            onClick={() => openEdit(policy)}
          >
            <Pencil size={16} />
          </Button>
          {policy.endsAt == null ? (
            <ButtonWithConfirmation
              message={
                isUpcoming
                  ? 'Delete this cushion charge policy? It has not started, so it will be removed.'
                  : 'End this cushion charge policy now? Shifts that already started keep using it.'
              }
              onClick={handleDelete}
              variant="text"
              kind="small"
              aria-label="Delete cushion charge policy"
              title="Delete cushion charge policy"
              danger
              disabled={isDeleting}
            >
              <Trash size={16} />
            </ButtonWithConfirmation>
          ) : null}
        </Flex>
      </Flex>
    </Box>
  )
}

export const CushionChargePoliciesMain = () => {
  const { space } = useAppTheme()
  const {
    state: { policies, isPending, isError, errorMessage },
    actions: { refetch },
  } = useCushionChargePoliciesContextValue()
  const {
    createCushionChargePolicyFlow: { open: openCreate },
  } = useCushionChargePoliciesWorkflowsContextValue()

  return (
    <AccordionCard
      size="small"
      title={
        <Flex justifyContent="space-between" alignItems="center" gap={2}>
          <Text fontSize={2} color="lightText">
            Cushion Charge Policies
          </Text>
          <Button
            variant="primary"
            kind="small"
            style={{ paddingLeft: space[2], paddingRight: space[2] }}
            onClick={(event) => {
              event.stopPropagation()
              openCreate()
            }}
          >
            <Plus size={14} />
            <Text as="span" ml={1}>
              Add
            </Text>
          </Button>
        </Flex>
      }
      isAccordion
      defaultOpen={false}
    >
      <Text mb={3} fontSize={1} color="lightText">
        With no policy, cushion workers are not charged. A policy bills them
        once they stay past the window.
      </Text>
      {isPending ? <Loading /> : null}
      {isError ? (
        <Message variant="error" borderRadius="small">
          {errorMessage || 'Failed to load cushion charge policies.'}{' '}
          <Button kind="small" variant="text" onClick={() => refetch()}>
            Retry
          </Button>
        </Message>
      ) : null}
      {!isPending && !isError && policies.length === 0 ? (
        <Text>No cushion charge policy.</Text>
      ) : null}
      {!isPending && !isError
        ? policies.map((policy, index) => (
            <PolicyRow
              key={policy.id}
              policy={policy}
              isLast={index === policies.length - 1}
            />
          ))
        : null}
    </AccordionCard>
  )
}
